import { generateChart } from "./chartComponentGenerator";

type RawField = {
  name: string;
  displayName?: string;
  type: string;
  isMesurable?: boolean | null;
  isDimensional?: boolean | null;
  tag?: string;
  aliase: string;
  isNonPersistField:boolean;
};

const chartVariantMap: Record<
  string,
  { displayName: string; value: string }[]
> = {
  "Pie Chart": [
    { displayName: "Pie Chart", value: "Pie_Chart" },
    { displayName: "V Bar Chart", value: "Vertical_Bar_Chart" },
     { displayName: "H Bar Chart", value: "Horizontal_Bar_Chart" },
    { displayName: "Advanced Pie Chart", value: "Advanced_Pie_Chart" },
    { displayName: "Pie Grid", value: "Pie_Grid" },
    { displayName: "Tree Map", value: "tree-map" },
    { displayName: "Score Card", value: "number-card" },
    { displayName: "Gauge", value: "gauge" },

  ],
  "Grouped Charts": [
    { displayName: "Vertical Bar Chart", value: "Vertical_Bar_Chart" },
    { displayName: "Horizontal Bar Chart", value: "Horizontal_Bar_Chart" },
    {
      displayName: "Grouped Vertical Bar Chart",
      value: "Grouped_Vertical_Bar_Chart",
    },
    {
      displayName: "Grouped Horizontal Bar Chart",
      value: "Grouped_Horizontal_Bar_Chart",
    },
    {
      displayName: "Stacked Vertical Bar Chart",
      value: "Stacked_Vertical_Bar_Chart",
    },
    {
      displayName: "Stacked Horizontal Bar Chart",
      value: "Stacked_Horizontal_Bar_Chart",
    },
    {
      displayName: "Normalized Vertical Bar Chart",
      value: "Normalized_Vertical_Bar_Chart",
    },
    {
      displayName: "Normalized Horizontal Bar Chart",
      value: "Normalized_Horizontal_Bar_Chart",
    },

  ],
  "Line Chart": [
    { displayName: "Line Chart", value: "line-chart" },
    { displayName: "Polar Chart", value: "polar-chart" },
    { displayName: "Area Chart", value: "area-chart" },
    { displayName: "Stacked Area Chart", value: "area-chart-stacked" },
    { displayName: "Normalized Area Chart", value: "area-chart-normalized" },
    { displayName: "Heat Map", value: "heat-map" },
    { displayName: "Box Chart", value: "box-chart" },
  ],
  "Advanced Chart": [
    { displayName: "Tree Map", value: "tree-map" },
    { displayName: "Score Card", value: "number-card" },
    { displayName: "Gauge", value: "gauge" },
  ],
    "Timeline": [
    { displayName: "Calendar", value: "Calendar" },
    { displayName: "Timeline", value: "Timeline" },

  ],
};

export type ChartDefinition = {
  chartType: string;
  chartVariations: string[];
  measureField: RawField[];
  dimensionField: RawField[];
  dateField?: RawField[]; // ✅ changed from string to RawField[]
  name: string;
  title: string;
  shortTitle: string;
  aggregation: string;
  selectedVariation?: string;
  isOptionChart?: string;
  queryConfig?: any;
  component?: any;
  mockData?: any;
};

function toTitleCase(str: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function toCamelCase(str: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word, i) => (i === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join("");
}

function generateShortTitle(
  chartVariation: string,
  measures: string[],
  dimensions: string[]
): string {
  const measureLabel = measures.map(toTitleCase).join(" & ");
  const dimensionLabel = dimensions.map(toTitleCase).join(" / ");
  return `${measureLabel} vs ${dimensionLabel}`;
}

function generateChartTitle(
  chartVariation: string,
  measures: string[],
  dimensions: string[],
  dateField?: string
): string {
  const measureLabel = measures.map(toTitleCase).join(" and ");
  const dimensionLabel = dimensions.map(toTitleCase).join(" and ");
  const chartLabel = toTitleCase(chartVariation);

  if (dateField) {
    return `${measureLabel} by ${dimensionLabel} (${chartLabel} over ${toTitleCase(
      dateField
    )})`;
  } else {
    return `${measureLabel} by ${dimensionLabel} (${chartLabel})`;
  }
}

function generateChartName(
  variation: string,
  measures: string[],
  dimensions: string[],
  dateField?: string
): string {
  const measureLabel = measures.map(toCamelCase).join("And");
  const dimensionLabel = dimensions.map(toCamelCase).join("And");
  const dateLabel = dateField ? `Over${toCamelCase(dateField)}` : "";
  return `${toCamelCase(variation)}By${dimensionLabel}${
    dateLabel ? `_${dateLabel}` : ""
  }_${measureLabel}`;
}

export function getChartVariants(
  fields: RawField[],
  modelName: string
): ChartDefinition[] {
const dimensionFields = fields.filter(
  (f) =>
    f.isDimensional === true &&
    !["date", "datetime"].includes(f.type?.toLowerCase()) &&
    f.isNonPersistField !== true
);

const dateFields = fields.filter(
  (f) =>
    f.isDimensional === true &&
    ["date", "datetime"].includes(f.type?.toLowerCase()) &&
    f.isNonPersistField !== true
);

const measureFields = fields.filter(
  (f) =>
    (f.isMesurable === true ||
      ["number", "integer", "float", "decimal"].includes(f.type?.toLowerCase())) &&
    f.isNonPersistField !== true
);
  const results: ChartDefinition[] = [];

  function pushVariant(
  chartType: string,
  variations: string[],
  measures: RawField[],
  dimensions: RawField[],
  dateField?: RawField[] // ✅ updated
) {

  const measureNames = measures.map((m) => m.name);
  const dimensionNames = dimensions.map((d) => d.name);

  if (
    measures.some((m) => ["date", "datetime"].includes(m.type?.toLowerCase()))
  ) {
    return;
  }

  const effectiveVariations = variations.length
    ? variations
    : chartVariantMap[chartType]?.map((v) => v.value) || [];

  const name = generateChartName(
    effectiveVariations[0],
    measureNames,
    dimensionNames,
    dateField?.[0]?.name // ✅ pick only the first item for name/title
  );
  const title = generateChartTitle(
    effectiveVariations[0],
    measureNames,
    dimensionNames,
    dateField?.[0]?.name
  );
  const shortTitle = generateShortTitle(
    effectiveVariations[0],
    measureNames,
    dimensionNames
  );

  const aggregation =
    measures[0]?.tag === "System"
      ? "count"
      : ["number", "integer", "float", "decimal"].includes(
          measures[0]?.type?.toLowerCase()
        )
      ? "sum"
      : "count";

  results.push({
    chartType,
    chartVariations: effectiveVariations,
    measureField: measures,
    dimensionField: dimensions,
    dateField, // ✅ assign as array
    name,
    title,
    shortTitle,
    aggregation,
    selectedVariation: effectiveVariations[0],
    isOptionChart: effectiveVariations[0],
    queryConfig: {
      model: modelName,
      measureFieldAggregation: aggregation,
      measureField: measures,
      dimensionField: dimensions,
      dateField: dateField ?? [], // ✅ array form
      conditions: [],
    },
  });
}

  const pieTypes = chartVariantMap["Pie Chart"].map((v) => v.value);
  dimensionFields.forEach((dim) =>
    measureFields.forEach((measure) => {
     // measure.displayName = measure.name;
      pushVariant("Standard Charts", pieTypes, [measure], [dim]);
    })
  );

  // we may not need this as this can be part of pie varient.
  // dimensionFields.forEach((dim) =>
  //   measureFields.forEach((measure) => {
  //     pushVariant("Bar Chart", [], [measure], [dim]);
  //   })
  // );

  if (dimensionFields.length >= 2) {
    const groupedDims = dimensionFields.slice(0, 2);
    measureFields.forEach((measure) => {
      pushVariant("Grouped Charts", [], [measure], groupedDims);
    });
  }

if (dateFields.length > 0) {
  for (const dateField of dateFields) {
    dimensionFields.forEach((dim) => {
      // 1️⃣ One chart per measure
      measureFields.forEach((measure) => {
        pushVariant(
          "Line Chart",
          [],
          [measure],
          [dim],
          [dateField]
        );
      
      });

      if(dateFields.length > 0){
          pushVariant(
          "Timeline",
          [],
          [],
           [dim],
          [dateField]
        
        );
      }

      // 2️⃣ One chart for all measures with the current dimension
      if (measureFields.length > 1) {
        pushVariant(
          "Line Chart",
          [],
          measureFields,
          [dim],
          [dateField]
        );
      }
    });
  }
}
  // this is already part of pie chart
  // const advTypes = chartVariantMap["Advanced Chart"].map((v) => v.value);
  // dimensionFields.forEach((dim) =>
  //   measureFields.forEach((measure) => {
  //     pushVariant("Advanced Chart", advTypes, [measure], [dim]);
  //   })
  // );

  return results;
}
